<template>
  <VitePwaManifest />
  <NuxtLayout>
    <NuxtPage />
    <client-only>
      <teleport to="body">
        <LazyBaseModal v-if="dialogStore.showDialog" />
      </teleport>
    </client-only>
  </NuxtLayout>
  <client-only>
    <teleport to="body">
      <LazyBasePopup v-if="globalPopupState.component" />
    </teleport>
  </client-only>
  <SpeedInsights />
</template>

<script setup lang="ts">
import { SpeedInsights } from '@vercel/speed-insights/vue';
import LazyBasePopup from '@/components/popup/LazyBasePopup.vue';
import LazyBaseModal from '@/components/modal/LazyBaseModal.vue';
import { useGrowthBook } from '@/composables/growthBook';
import { useClickID } from '@/composables/clickId';
import { useCustomGtag } from '~/composables/gtag.ts';
import { useDialogStore } from '~/stores/dialog.ts';
import { useMixpanel } from '~/composables/mixpanel.ts';
import 'dayjs/locale/de';
import 'dayjs/locale/es';
import 'dayjs/locale/fr';
import 'dayjs/locale/it';
import 'dayjs/locale/es-mx';
import 'dayjs/locale/pt';
import 'dayjs/locale/pt-br';
import { useUserData } from '~/composables/userData.ts';
import { useOpenReplay } from '~/composables/openReplay.ts';

const route = useRoute();
const router = useRouter();
const {
  $db,
  $mixpanel,
  $versions,
  $sentrySetUser,
  $snapTrack,
  $releaseVersion,
} = useNuxtApp();
const store = useKegelStore();
const dialogStore = useDialogStore();
const { globalPopupState } = usePopups();
const { initPixel } = useFacebookPixel();
const { initGrowthBook, experimentId, variation } = useGrowthBook();
const { sendAttribution } = useMixpanel();
const localePath = useLocalePath();
const { track } = useRedTrack();
const { getUserData } = useUserData();
const { openReplayTracker } = useOpenReplay();

const { locale } = useI18n();

if (import.meta.client) {
  $mixpanel?.people.set({ 'Release version': $releaseVersion });
  const email = await $db.getItem('user_email') as string;
  const uuid = await $db.getItem('user-uuid') as string;

  if (email) {
    store.userEmail = email;
  }

  if (uuid) {
    store.userUuid = uuid;
  }

  initPixel();
  await getUserData();
  $snapTrack('PAGE_VIEW', {});
  await initGrowthBook();
}

useSeoMeta({
  title: '',
  ogTitle: '',
  description: 'Improve  in 5 min/day. Feel confident & happy with the most downloaded intimate wellness app in the World',
  ogDescription: 'Improve  in 5 min/day. Feel confident & happy with the most downloaded intimate wellness app in the World.',
});

onMounted(async () => {
  if (import.meta.client) {
    const { main_goal, secondary_goal } = route.query;

    checkQueryParams();
    setTimeout(() => {
      const { initGtag } = useCustomGtag();
      initGtag();
    }, 5000);
    initTiktokId();
    setUserDataToTrackers();

    if (main_goal) {
      $db.setItem('goals', { main: main_goal, additional: secondary_goal });
    }
    const { getClickID } = useClickID();
    const clickId = await getClickID(experimentId, variation) as string;

    const isAttributionWasSent = route.query.attribution || await $db?.getItem('attributesWasSet');
    store.currentExperimentKey = experimentId.value as string;
    store.currentExperimentVariation = variation.value as string;
    $db.setItem('experiment', { experimentId: experimentId.value, variation: variation.value });
    store.clickId = clickId;

    if ($versions.value.trackOnlyAfterQuiz !== '0' && !isAttributionWasSent) {
      await sendAttribution(clickId, experimentId.value, variation.value);
    }
    const queries: { [key: string]: string } = {
      experimentId: experimentId.value as string,
      variation: variation.value as string,
    };
    if (clickId && route.query.rtkcid !== clickId) {
      queries.rtkcid = clickId;
    }
    const dbAttribution = await $db?.getItem('attributesWasSet');
    if ((!route.query.attribution && dbAttribution)) {
      queries.attribution = '1';
    }

    const newQueriesValues = Object.values({ ...route.query, ...queries }).filter(item => item !== undefined);
    const queriesValues = Object.values(route.query);

    if (newQueriesValues.length !== queriesValues.length) {
      router.push(localePath({ path: route.path, query: { ...route.query, ...queries } }));
    }

    if (route.path === `/${locale.value}` && !route.query.attribution) {
      await track('Quiz_Start_Screen_Shown');
    }
  }
});

function checkQueryParams(): void {
  const userId = route.query?.user_uuid as string || route.query?.web_user_id as string;
  if (userId) {
    store.userUuid = userId;
    $db.setItem('user-uuid', userId);
  }
  if (route.query?.email) {
    store.userEmail = route.query?.email as string;
    $db.setItem('user_email', route.query?.email as string);
  }
}

function initTiktokId() {
  const refId = String(route.query.ref_id);
  if (refId && validateECP(refId)) {
    $db.setItem('tiktok_click_id', refId);
  }
}

function validateECP(ecp: string): boolean {
  const pattern = /^E_C_P_/;
  return pattern.test(ecp);
}

function setUserDataToTrackers(): void {
  if ($versions.value.trackOnlyAfterQuiz !== '0') {
    $mixpanel.identify(store.userUuid);
    $mixpanel.people.set({ $email: store.userEmail });
  }

  openReplayTracker().setUserID(store.userUuid);
  $mixpanel.people.set({ 'Quiz Language': locale.value?.toUpperCase() });
  if ($versions?.value.paymentForm === '3') {
    $mixpanel.people.set({ gateway: 'paddle' });
  }
  openReplayTracker().setMetadata('language', locale.value);
  $sentrySetUser({ id: store.userUuid });

  if (store.userEmail) {
    openReplayTracker().setMetadata('email', store.userEmail);
  }
}
</script>

<style lang="scss">
@use 'assets/styles/animations';

.hidden-text {
  position: fixed;
  top: -50px;
  color: transparent;
}

.paragraph-1 {
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 21px;
}

.paragraph-2 {
  font-size: 14px;
  font-style: normal;
  font-weight: 300;
  line-height: 21px;
}
</style>
